import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, Check, CheckCheck, Trash2, AlertTriangle, Info, CheckCircle, Shield, Clock, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { cn } from '../lib/utils';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Priority-based notification routing
 *
 * Priority 1 — backend action_url (stamped at creation time for all NEW notifications)
 *   Already includes the correct tab path, e.g. /dashboard?tab=manager-leave-approvals
 *
 * Priority 2 — recipient_role + resource_type (for OLD notifications without action_url)
 *   The recipient_role field tells us the audience tier the notification was created for:
 *     "admin"    → HR-admin views in the Admin Dashboard
 *     "manager"  → Manager views in the Admin Dashboard
 *     "employee" → Employee portal pages
 *
 * Priority 3 — resource_type exact match (employee-side fallback)
 * Priority 4 — notification_type prefix match (employee-side fallback)
 * Priority 5 — /notifications (catch-all)
 */

// --- Priority 2 role-aware route tables (for old notifications without action_url) ---

// Admin-tagged: HR admin viewing org-wide items on the dashboard
const ADMIN_RESOURCE_ROUTES = {
  leave_request:  '/dashboard?tab=leave-management',
  leave:          '/dashboard?tab=leave-management',
  swipe_request:  '/dashboard?tab=swipe-approvals',
  swipe:          '/dashboard?tab=swipe-approvals',
  od_request:     '/dashboard?tab=od-approvals',
  reimbursement:  '/dashboard?tab=reimbursements',
};

// Manager-tagged: manager reviewing team requests on the dashboard
const MANAGER_RESOURCE_ROUTES = {
  leave_request:  '/dashboard?tab=manager-leave-approvals',
  leave:          '/dashboard?tab=manager-leave-approvals',
  swipe_request:  '/dashboard?tab=swipe-approvals',
  swipe:          '/dashboard?tab=swipe-approvals',
  od_request:     '/dashboard?tab=od-approvals',
  reimbursement:  '/dashboard?tab=manager-reimbursements',
};

// Admin notification_type prefix → dashboard tab
const ADMIN_TYPE_PREFIX_ROUTES = [
  { prefix: 'leave',         route: '/dashboard?tab=leave-management' },
  { prefix: 'swipe',         route: '/dashboard?tab=swipe-approvals' },
  { prefix: 'od',            route: '/dashboard?tab=od-approvals' },
  { prefix: 'reimbursement', route: '/dashboard?tab=reimbursements' },
];

// Manager notification_type prefix → dashboard tab
const MANAGER_TYPE_PREFIX_ROUTES = [
  { prefix: 'leave',         route: '/dashboard?tab=manager-leave-approvals' },
  { prefix: 'swipe',         route: '/dashboard?tab=swipe-approvals' },
  { prefix: 'od',            route: '/dashboard?tab=od-approvals' },
  { prefix: 'reimbursement', route: '/dashboard?tab=manager-reimbursements' },
];

// --- Priority 3-4 employee-side tables (used when role is employee/null) ---
const EMPLOYEE_RESOURCE_ROUTES = {
  leave:             '/my-leave?tab=applications',
  leave_request:     '/my-leave?tab=applications',
  payslip:           '/my-payslips',
  salary:            '/my-payslips',
  payroll_record:    '/my-payslips',
  payroll:           '/my-payslips',
  loan:              '/my-loans',
  swipe_correction:  '/my-swipe-requests',
  swipe_request:     '/my-swipe-requests',
  swipe:             '/my-swipe-requests',
  od_request:        '/my-od-requests',
  reimbursement:     '/my-reimbursements',
  document:          '/my-documents',
  admin_broadcast:   '/notifications',
  broadcast:         '/notifications',
};

const EMPLOYEE_TYPE_PREFIX_ROUTES = [
  { prefix: 'leave',         route: '/my-leave?tab=applications' },
  { prefix: 'payslip',       route: '/my-payslips' },
  { prefix: 'salary',        route: '/my-payslips' },
  { prefix: 'loan',          route: '/my-loans' },
  { prefix: 'swipe',         route: '/my-swipe-requests' },
  { prefix: 'od',            route: '/my-od-requests' },
  { prefix: 'reimbursement', route: '/my-reimbursements' },
  { prefix: 'document',      route: '/my-documents' },
  { prefix: 'broadcast',     route: '/notifications' },
];

function getRouteForNotification(notification) {
  // ── Priority 1: explicit action_url from backend ────────────────────────────
  // New notifications always have this stamped at creation time.
  if (notification.action_url) {
    try {
      // Full URL (e.g. http://host/path?tab=x) → extract pathname + search
      const u = new URL(notification.action_url);
      return u.pathname + u.search;
    } catch {
      // Already a relative path: "/dashboard?tab=manager-leave-approvals"
      return notification.action_url;
    }
  }

  // ── Priority 2: recipient_role + resource_type for old notifications ────────
  const role = (notification.recipient_role || '').toLowerCase();
  const rt   = (notification.resource_type  || '').toLowerCase();
  const nt   = (notification.notification_type || notification.type || '').toLowerCase();

  if (role === 'admin') {
    if (rt && ADMIN_RESOURCE_ROUTES[rt]) return ADMIN_RESOURCE_ROUTES[rt];
    for (const { prefix, route } of ADMIN_TYPE_PREFIX_ROUTES) {
      if (nt.startsWith(prefix)) return route;
    }
    return '/dashboard';  // admin fallback — open dashboard, they can navigate
  }

  if (role === 'manager') {
    if (rt && MANAGER_RESOURCE_ROUTES[rt]) return MANAGER_RESOURCE_ROUTES[rt];
    for (const { prefix, route } of MANAGER_TYPE_PREFIX_ROUTES) {
      if (nt.startsWith(prefix)) return route;
    }
    return '/dashboard';  // manager fallback
  }

  // ── Priority 3-4: employee-side routes (role === 'employee' or untagged) ───
  if (rt && EMPLOYEE_RESOURCE_ROUTES[rt]) return EMPLOYEE_RESOURCE_ROUTES[rt];
  for (const { prefix, route } of EMPLOYEE_TYPE_PREFIX_ROUTES) {
    if (nt.startsWith(prefix)) return route;
  }

  // ── Priority 5: catch-all ───────────────────────────────────────────────────
  return '/notifications';
}

// --- Portal-aware remap (applied after the 5-priority resolution above) ------

/**
 * Both portals have an approvals surface for the same requests:
 *   Admin Dashboard     → /dashboard?tab=<tab>
 *   Employee Portal     → /my-leave-approvals?type=<type>
 *
 * The backend always stamps action_url with the ADMIN route, so an approver
 * standing in the Employee Portal would otherwise get bounced out of it. These
 * tables translate a resolved route between the two surfaces.
 */
const EMPLOYEE_APPROVALS_PATH = '/my-leave-approvals';

const ADMIN_TAB_TO_APPROVAL_TYPE = {
  'leave-management':        'leave',
  'manager-leave-approvals': 'leave',
  'swipe-approvals':         'swipe',
  'od-approvals':            'od',
  'reimbursements':          'reimbursement',
  'manager-reimbursements':  'reimbursement',
};

// Which dashboard tab an approval type maps back to, per audience tier.
const APPROVAL_TYPE_TO_ADMIN_TAB = {
  leave:         { admin: 'leave-management', manager: 'manager-leave-approvals' },
  swipe:         { admin: 'swipe-approvals',  manager: 'swipe-approvals' },
  od:            { admin: 'od-approvals',     manager: 'od-approvals' },
  reimbursement: { admin: 'reimbursements',   manager: 'manager-reimbursements' },
};

// Employee Portal paths. Anything else (notably /dashboard) is the admin portal.
const EMPLOYEE_PORTAL_PATH_PREFIXES = ['/employee-portal', '/my-'];

export function isEmployeePortalPath(pathname) {
  const p = pathname || '';
  return EMPLOYEE_PORTAL_PATH_PREFIXES.some((prefix) => p.startsWith(prefix));
}

/**
 * Rewrite a resolved notification route so it lands on the approvals surface of
 * the portal the user is currently in, and deep-link to the specific request.
 *
 * Rules:
 *  - No admin access at all → always the Employee Portal (the dashboard would
 *    just be blocked).
 *  - In the Employee Portal → stay there, EXCEPT for `recipient_role: 'admin'`
 *    notifications, which are org-wide HR items that may not appear in
 *    /my-leave-approvals (it lists only requests where you are the approver).
 *  - In the Admin Dashboard → stay there.
 *  - Non-approval routes are returned untouched.
 */
function remapRouteForPortal(route, notification, { inEmployeePortal, hasAdminAccess }) {
  let path = route;
  let search = '';
  const qIndex = route.indexOf('?');
  if (qIndex !== -1) {
    path   = route.slice(0, qIndex);
    search = route.slice(qIndex + 1);
  }
  const params = new URLSearchParams(search);
  const tier   = (notification.recipient_role || '').toLowerCase();

  // Resolve the approval type this route points at, whichever portal it targets.
  let type = null;
  if (path === '/dashboard') {
    type = ADMIN_TAB_TO_APPROVAL_TYPE[params.get('tab') || ''] || null;
  } else if (path === EMPLOYEE_APPROVALS_PATH) {
    type = params.get('type') || null;
  }
  if (!type || !APPROVAL_TYPE_TO_ADMIN_TAB[type]) return route;

  // An admin-tier item stays on the dashboard whenever the user can open it.
  const keepAdmin = !hasAdminAccess ? false : (!inEmployeePortal || tier === 'admin');

  const next = new URLSearchParams();
  if (keepAdmin) {
    const tabs = APPROVAL_TYPE_TO_ADMIN_TAB[type];
    next.set('tab', tier === 'admin' ? tabs.admin : tabs.manager);
    path = '/dashboard';
  } else {
    next.set('type', type);
    path = EMPLOYEE_APPROVALS_PATH;
  }
  // Deep-link to the originating request so the target page can open its row.
  if (notification.resource_id) next.set('request', notification.resource_id);

  return `${path}?${next.toString()}`;
}

const NotificationCenter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { getAvailableRoles } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  // Tracks which notification ID has a pending API action (prevents double-clicks)
  const [actionPending, setActionPending] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = (nid) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(nid)) next.delete(nid);
      else next.add(nid);
      return next;
    });
  };

  // When the account is suspended (402 PAYMENT_REQUIRED), stop all polling so we
  // don't flood the backend with requests that will all return 402.
  const pollingSuspendedRef = useRef(false);

  useEffect(() => {
    const stopPolling = () => { pollingSuspendedRef.current = true; };
    window.addEventListener('vivya:payment-required', stopPolling);
    return () => window.removeEventListener('vivya:payment-required', stopPolling);
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (pollingSuspendedRef.current) return;
    try {
      setLoading(true);
      const response = await api.get('/api/admin-notifications?limit=20');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      if (error.response?.status === 402) {
        pollingSuspendedRef.current = true; // stop future fetches
        return;
      }
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    if (pollingSuspendedRef.current) return; // account suspended — skip
    try {
      const response = await api.get('/api/admin-notifications/unread-count', {
        timeout: 5000  // 5 second timeout for quick polling
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      if (error.response?.status === 402) {
        // Account suspended — stop the interval poller permanently for this session
        pollingSuspendedRef.current = true;
        return;
      }
      // Silently fail for timeout errors to avoid console spam
      if (error.code !== 'ECONNABORTED') {
        console.error('Failed to fetch unread count:', error);
      }
      // Keep existing count on error
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000); // Poll every 60 seconds (F6: reduced frequency)
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    if (actionPending === notificationId) return; // prevent double-clicks
    setActionPending(notificationId);
    try {
      await api.post(`/api/admin-notifications/${notificationId}/read`);
      setNotifications(prev => {
        const wasUnread = prev.find(n => n.notification_id === notificationId && !n.is_read);
        if (wasUnread) setUnreadCount(c => Math.max(0, c - 1));
        return prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n);
      });
    } catch (error) {
      console.error('Failed to mark as read:', error);
    } finally {
      setActionPending(null);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/api/admin-notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const dismissNotification = async (notificationId) => {
    if (actionPending === notificationId) return; // prevent double-clicks
    setActionPending(notificationId);
    try {
      await api.post(`/api/admin-notifications/${notificationId}/dismiss`);
      setNotifications(prev => {
        const dismissed = prev.find(n => n.notification_id === notificationId);
        // Decrement badge immediately if we're removing an unread notification
        if (dismissed && !dismissed.is_read) {
          setUnreadCount(c => Math.max(0, c - 1));
        }
        return prev.filter(n => n.notification_id !== notificationId);
      });
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
    } finally {
      setActionPending(null);
    }
  };

  const clearAllNotifications = async () => {
    try {
      await api.post('/api/admin-notifications/dismiss-all');
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to clear all notifications:', error);
    }
  };

  /**
   * Clicking a notification card:
   *  1. Marks it as read (fire-and-forget — don't block navigation)
   *  2. Closes the dropdown
   *  3. Navigates to the relevant page
   */
  const handleNotificationClick = (notification) => {
    const notifId = notification.notification_id;
    // Mark as read in the background (don't await — navigation should feel instant)
    if (!notification.is_read) {
      markAsRead(notifId).catch(() => {});
    }
    setIsOpen(false);
    const route = getRouteForNotification(notification);
    navigate(remapRouteForPortal(route, notification, {
      inEmployeePortal: isEmployeePortalPath(location.pathname),
      hasAdminAccess:   getAvailableRoles().some((r) => r.id === 'admin'),
    }));
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'warning': return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'success': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'security_alert': return <Shield className="h-4 w-4 text-red-500" />;
      case 'approval_required': return <Clock className="h-4 w-4 text-blue-500" />;
      default: return <Info className="h-4 w-4 text-blue-500" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 border-red-300';
      case 'high': return 'bg-orange-50 border-orange-200';
      case 'medium': return 'bg-blue-50 border-blue-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diff = now - date;
    if (diff < 0) return t('notificationCenter.justNow'); // guard for clock skew
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t('notificationCenter.justNow');
    if (minutes < 60) return t('notificationCenter.minutesAgo', { count: minutes });
    if (hours < 24) return t('notificationCenter.hoursAgo', { count: hours });
    if (days < 7) return t('notificationCenter.daysAgo', { count: days });
    return date.toLocaleDateString();
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold">{t('notificationCenter.title')}</h3>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs h-7 px-2">
                <CheckCheck className="h-3 w-3 mr-1" />
                {t('notificationCenter.markAllRead')}
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllNotifications}
                className="text-xs h-7 px-2 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
                title={t('notificationCenter.deleteAllTitle')}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                {t('notificationCenter.clearAll')}
              </Button>
            )}
          </div>
        </div>
        <ScrollArea className="h-[400px]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-500 dark:text-gray-300">
              <Bell className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">{t('notificationCenter.noNotifications')}</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => {
                const nid = notification.notification_id;
                const isPending = actionPending === nid;
                // Unread: blue left border; read + priority bg only when no unread highlight
                const unreadClass = !notification.is_read
                  ? 'border-l-4 border-l-blue-500 bg-blue-50/50 dark:bg-blue-950/20'
                  : cn('border-l-4', getPriorityColor(notification.priority));
                return (
                  <div
                    key={nid}
                    className={cn(
                      'p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer',
                      unreadClass,
                      isPending && 'opacity-60 pointer-events-none'
                    )}
                    onClick={() => !isPending && handleNotificationClick(notification)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification.notification_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={cn(
                          "text-sm",
                          !notification.is_read && "font-medium"
                        )}>
                          {notification.title}
                        </p>
                        <p
                          id={`notification-message-${nid}`}
                          className={cn(
                            'text-xs text-gray-600 dark:text-gray-200 mt-1 whitespace-pre-line break-words',
                            !expandedIds.has(nid) && 'line-clamp-2'
                          )}
                        >
                          {notification.message}
                        </p>
                        {notification.message && (notification.message.length > 120 || notification.message.includes('\n')) && (
                          <button
                            type="button"
                            className="mt-1 text-xs font-medium text-indigo-700 dark:text-indigo-300 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded"
                            aria-expanded={expandedIds.has(nid)}
                            aria-controls={`notification-message-${nid}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpanded(nid);
                            }}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            {expandedIds.has(nid) ? t('notificationCenter.showLess') : t('notificationCenter.showMore')}
                          </button>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-gray-500 dark:text-gray-300">
                            {formatTime(notification.created_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex-shrink-0 flex items-center gap-1">
                        {!notification.is_read && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            title={t('notificationCenter.markAsReadTitle')}
                            disabled={isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(nid);
                            }}
                          >
                            <Check className="h-3 w-3" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                          title={t('notificationCenter.deleteNotificationTitle')}
                          disabled={isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            dismissNotification(nid);
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
        {/* ── View all footer ── */}
        <div className="border-t">
          <button
            className="w-full flex items-center justify-center gap-1 py-2.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
            onClick={() => { setIsOpen(false); navigate('/notifications'); }}
          >
            {t('notificationCenter.viewAll')}
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationCenter;

